import React, { useState, useContext, useEffect } from "react";
import {
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaFilter, FaSync, FaSearch } from "react-icons/fa";
import { BsLightning } from "react-icons/bs";
import { TABLE_COLUMN_WIDTHS } from "../metadataTableUtils";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { tagService, metadataService } from "../../../../../../services/api";
import { TagScore } from "../../../../../utils/TagScore";
import * as Sentry from "@sentry/react";

const TableHeader = ({
  displayColumns,
  deasyFileKey,
  loading,
  totalRows,
  handleRefresh,
  inputSearchQuery,
  setInputSearchQuery,
  setPage,
  setSearchQuery,
  handleFilterClick,
  effectiveFilters,
  renderDistributionChart,
  onStandardizeClick,
  onFindReplaceClick,
  handleOpenQueryDialog,
  dataslice_id = null,
}) => {
  const { deasyApiKey, vdbLastActiveProfileName } = useContext(BaseContext);
  const [tagTotals, setTagTotals] = useState({});

  const handleSearch = () => {
    setPage(0);
    setSearchQuery(inputSearchQuery);
  };

  const handleClearSearch = () => {
    setPage(0);
    setSearchQuery("");
    setInputSearchQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const [tagScores, setTagScores] = useState({});

  const formatFiltersForApi = (filters) => {
    if (!filters || Object.keys(filters).length === 0) {
      return null;
    }

    return Object.entries(filters)
      .map(([tagId, values]) => {
        const valueArray = Array.isArray(values) ? values : [values];

        if (valueArray.length === 0) {
          return null;
        }

        const firstValue = valueArray[0];
        const hasOperator =
          typeof firstValue === "object" &&
          firstValue !== null &&
          "value" in firstValue &&
          "operator" in firstValue;

        if (hasOperator) {
          return {
            tag_id: tagId,
            values: valueArray.map((v) => v.value),
            operator: firstValue.operator,
          };
        } else {
          return {
            tag_id: tagId,
            values: valueArray,
            operator: "in",
          };
        }
      })
      .filter(Boolean);
  };

  useEffect(() => {
    const fetchTagScores = async () => {
      try {
        const response = await tagService.getTagsScores(
          displayColumns,
          vdbLastActiveProfileName,
          deasyApiKey,
        );
        setTagScores(response.data.scores);
      } catch (error) {
        console.error("Error fetching tag scores:", error);
        setTagScores({});
      }
    };

    if (vdbLastActiveProfileName && deasyApiKey) {
      fetchTagScores();
    }
  }, [displayColumns, vdbLastActiveProfileName, deasyApiKey]);

  useEffect(() => {
    const fetchTagTotals = async () => {
      try {
        const formattedFilters = formatFiltersForApi(effectiveFilters);

        const response = await metadataService.getTagTotals(
          vdbLastActiveProfileName,
          deasyApiKey,
          dataslice_id,
          formattedFilters,
          displayColumns,
        );
        setTagTotals(response.data.tag_totals || {});
      } catch (error) {
        console.error("Error fetching tag totals:", error);
        setTagTotals({});
      }
    };

    if (
      displayColumns.length > 0 &&
      vdbLastActiveProfileName &&
      deasyApiKey &&
      !loading
    ) {
      fetchTagTotals();
    }
  }, [
    displayColumns,
    vdbLastActiveProfileName,
    deasyApiKey,
    loading,
    handleRefresh,
    effectiveFilters,
    dataslice_id,
  ]);

  return (
    <TableHead>
      <TableRow>
        {displayColumns.map((column) => (
          <TableCell
            key={column}
            sx={{
              width:
                column === deasyFileKey
                  ? TABLE_COLUMN_WIDTHS[deasyFileKey]
                  : TABLE_COLUMN_WIDTHS.default,
              minWidth:
                column === deasyFileKey
                  ? TABLE_COLUMN_WIDTHS[deasyFileKey]
                  : TABLE_COLUMN_WIDTHS.default,
              maxWidth:
                column === deasyFileKey
                  ? TABLE_COLUMN_WIDTHS[deasyFileKey]
                  : TABLE_COLUMN_WIDTHS.default,
              padding: "8px 16px",
              backgroundColor: "#f9fafb",
              fontWeight: 500,
              fontSize: "0.8125rem",
              height: column !== deasyFileKey ? "120px" : "48px",
              lineHeight: 1.2,
              color: "#374151",
              position: "sticky",
              top: 0,
              ...(column === deasyFileKey && {
                left: 0,
                zIndex: 3,
              }),
              ...(column !== deasyFileKey && {
                zIndex: 2,
              }),
              overflow: "visible",
            }}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between gap-2 mb-2">
                {column === deasyFileKey ? (
                  <FileNameColumn
                    loading={loading}
                    totalRows={totalRows}
                    handleRefresh={handleRefresh}
                    inputSearchQuery={inputSearchQuery}
                    setInputSearchQuery={setInputSearchQuery}
                    handleSearch={handleSearch}
                    handleClearSearch={handleClearSearch}
                    handleKeyDown={handleKeyDown}
                  />
                ) : (
                  <HoverRevealColumn
                    column={column}
                    handleFilterClick={handleFilterClick}
                    effectiveFilters={effectiveFilters}
                    onStandardizeClick={onStandardizeClick}
                    onFindReplaceClick={onFindReplaceClick}
                    tagScore={tagScores?.[column] ?? null}
                    loading={loading}
                  />
                )}
              </div>
              {column !== deasyFileKey && (
                <div className="flex flex-col">
                  <div className="h-[80px] relative">
                    {renderDistributionChart(column)}
                  </div>
                  {totalRows > 0 && (
                    <div className="flex justify-end mt-1">
                      {(() => {
                        const percentage = Math.round(
                          ((tagTotals?.[column] || 0) / totalRows) * 100,
                        );

                        // Validate percentage range
                        if (percentage < 0 || percentage > 100) {
                          Sentry.captureException(
                            new Error(
                              `Invalid coverage percentage: ${percentage}% for column ${column}. tagTotals: ${tagTotals?.[column]}, totalRows: ${totalRows}`,
                            ),
                          );
                          return null;
                        }

                        return (
                          <Tooltip
                            title={`${percentage}% of files have this tag`}
                          >
                            <span className="text-xs text-gray-500 font-medium">
                              {percentage}% coverage
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const FileNameColumn = ({
  loading,
  totalRows,
  handleRefresh,
  inputSearchQuery,
  setInputSearchQuery,
  handleSearch,
  handleClearSearch,
  handleKeyDown,
}) => (
  <div className="flex items-start justify-between w-full flex-col gap-4">
    <div className="flex items-center gap-4">
      <span className="text-sm font-medium whitespace-nowrap">File Name</span>
      <div className="flex items-center gap-3 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
        <span className="text-base font-semibold text-gray-900">
          {loading ? "..." : totalRows}
        </span>
        <span className="text-sm text-gray-600 whitespace-nowrap">files</span>
        <div className="h-6 w-[1px] bg-gray-200 mx-2"></div>

        <Tooltip title="Refresh data and clear filters">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={loading}
            sx={{
              padding: 0.5,
              backgroundColor: "white",
              border: "1px solid #E5E7EB",
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            <FaSync
              className={loading ? "animate-spin" : ""}
              size={14}
              color="#374151"
            />
          </IconButton>
        </Tooltip>
      </div>
    </div>
    <div className="flex gap-2 w-full">
      <div className="relative flex-1">
        <input
          type="text"
          placeholder="Search files..."
          value={inputSearchQuery}
          onChange={(e) => setInputSearchQuery(e.target.value)}
          className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-16"
          onKeyDown={handleKeyDown}
        />
        {inputSearchQuery && (
          <button
            onClick={handleClearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSearch}
          className="group px-2.5 py-2 text-sm bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 hover:px-4 transition-all duration-300 ease-in-out font-medium flex items-center justify-center"
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-800"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Searching...
            </span>
          ) : (
            <span className="flex items-center">
              <svg
                className="flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <span className="w-0 overflow-hidden group-hover:w-auto group-hover:ml-1.5 transition-all duration-300 ease-in-out whitespace-nowrap">
                Search
              </span>
            </span>
          )}
        </button>
      </div>
    </div>
  </div>
);

const HoverRevealColumn = ({
  column,
  handleFilterClick,
  effectiveFilters,
  onStandardizeClick,
  onFindReplaceClick,
  tagScore,
  loading,
}) => {
  const hasActiveFilters = effectiveFilters[column]?.length > 0;

  return (
    <div className="flex flex-col w-full gap-1">
      {/* Top row: Confidence score (left) + Action buttons (right) */}
      <div className="flex items-center justify-between gap-2 relative">
        {/* Confidence score positioned top-left */}
        {tagScore !== null ? (
          <div className="flex-shrink-0">
            <TagScore tagScore={tagScore} tagType="file" />
          </div>
        ) : (
          <div />
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-0.5">
          <Tooltip title="Filter Values">
            <IconButton
              size="small"
              onClick={(e) => handleFilterClick(e, column)}
              disabled={loading}
              sx={{
                padding: 0.5,
                backgroundColor: hasActiveFilters ? "#dcfce7" : "transparent",
                border: hasActiveFilters
                  ? "1px solid #22c55e"
                  : "1px solid transparent",
                "&:hover": {
                  backgroundColor: hasActiveFilters ? "#bbf7d0" : "#f9fafb",
                  borderColor: hasActiveFilters ? "#16a34a" : "#e5e7eb",
                },
              }}
            >
              <FaFilter
                size={10}
                color={hasActiveFilters ? "#16a34a" : "#374151"}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Standardize Values">
            <IconButton
              size="small"
              onClick={() => onStandardizeClick(column)}
              disabled={loading}
              sx={{
                padding: 0.5,
                border: "1px solid transparent",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  borderColor: "#e5e7eb",
                },
              }}
            >
              <BsLightning size={10} color="#6b7280" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Find & Replace">
            <IconButton
              size="small"
              onClick={() => onFindReplaceClick(column)}
              disabled={loading}
              sx={{
                padding: 0.5,
                border: "1px solid transparent",
                "&:hover": {
                  backgroundColor: "#f9fafb",
                  borderColor: "#e5e7eb",
                },
              }}
            >
              <FaSearch size={9} color="#6b7280" />
            </IconButton>
          </Tooltip>

          {/* Active filter indicator */}
          {hasActiveFilters && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full border border-white z-10"></div>
          )}
        </div>
      </div>

      {/* Bottom row: Column name */}
      <div className="flex items-center w-full">
        <Tooltip
          title={column
            .replace(/_/g, " ")
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")}
        >
          <div className="font-bold text-gray-900 truncate flex-1">
            {column
              .replace(/_/g, " ")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")}
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

export default TableHeader;
